// frontend/src/components/OwnerDecodedSidebar.js  (replacement)
// Grouped nav, stroke icons instead of emoji, 1Parc chrome mark.
// Same feature-flag filtering and NavLink routing as before.

import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import IparcMark from './IparcMark';
import {
  IconHome, IconCar, IconCalendar, IconWrench, IconDoc, IconClipboard,
  IconTow, IconCard, IconBox, IconSpark, IconHelp, IconLogout,
} from './icons/Icons';

export default function OwnerDecodedSidebar({ className = '', vehicleCount, issueCount, onLogout }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();

  const sections = [
    {
      title: 'Pilotage',
      items: [
        { key: 'dashboard',  label: t('sidebar.ownerDashboard'), icon: IconHome,     path: '/dashboard' },
        { key: 'myVehicles', label: t('sidebar.myVehicles'),     icon: IconCar,      path: '/vehicles', count: vehicleCount },
        { key: 'dashboard',  label: 'Échéances',                 icon: IconCalendar, path: '/vehicles?filter=alerts', badge: issueCount },
      ],
    },
    {
      title: 'Atelier',
      items: [
        { key: 'requestWorkshop',   label: t('sidebar.requestWorkshop'),   icon: IconWrench,    path: '/book-workshop' },
        { key: 'requestQuotation',  label: t('sidebar.requestQuotation'),  icon: IconDoc,       path: '/quotation/request' },
        { key: 'myQuotations',      label: t('sidebar.myQuotations'),      icon: IconDoc,       path: '/owner/quotations' },
        { key: 'carReception',      label: t('sidebar.carReception'),      icon: IconClipboard, path: '/owner/reception-history' },
        { key: 'requestLoader',     label: t('sidebar.requestLoader'),     icon: IconTow,       path: '/request-loader' },
      ],
    },
    {
      title: 'Finances',
      items: [
        { key: 'myInvoices',          label: t('sidebar.myInvoices'),          icon: IconCard,  path: '/owner/invoices' },
        { key: 'clientInvoices',      label: t('sidebar.clientInvoices'),      icon: IconCard,  path: '/dashboard/invoices' },
        { key: 'supplierQuotations',  label: t('sidebar.supplierQuotations'),  icon: IconBox,   path: '/owner/supplier-quotations' },
        ...(user?.isCompany
          ? [{ key: 'purchaseOrders', label: t('sidebar.purchaseOrders'), icon: IconBox, path: '/dashboard/purchase-orders' }]
          : []),
        { key: 'aiAssistant',         label: t('sidebar.aiAssistant'),         icon: IconSpark, path: '/ai-assistant' },
      ],
    },
  ]
    .map(s => ({ ...s, items: s.items.filter(i => isFeatureEnabled(i.key)) }))
    .filter(s => s.items.length > 0);

  const linkClass = ({ isActive }) =>
    `relative flex items-center gap-[11px] px-2.5 py-[9px] rounded-[9px] text-[13px] transition-colors ${
      isActive
        ? 'bg-[#1E1B17] text-[#F4F1EA] font-medium'
        : 'text-[#9A9184] hover:bg-[#171512] hover:text-[#F4F1EA] font-normal'
    }`;

  return (
    <aside className={`w-[248px] h-full bg-[#100F0D] border-r border-[#211E1B] px-4 pt-[22px] pb-[18px]
                       flex flex-col gap-[22px] overflow-y-auto ${className}`}>

      <div className="flex items-center gap-[11px] px-1.5">
        <IparcMark size={38} />
        <div className="leading-[1.1]">
          <p className="font-['Space_Grotesk',sans-serif] text-sm font-semibold tracking-[.14em] text-[#F4F1EA]">IPARC</p>
          <p className="text-[9.5px] uppercase tracking-[.16em] text-[#6E665C] mt-0.5">Gestion de flotte</p>
        </div>
      </div>

      <div className="px-3 py-[11px] border border-[#241F1B] rounded-xl bg-gradient-to-b from-[#17140F] to-[#131110]">
        <p className="text-[9px] uppercase tracking-[.18em] text-[#6E665C] mb-[5px]">Compte flotte</p>
        <p className="font-['Space_Grotesk',sans-serif] text-[13.5px] font-semibold tracking-[-.01em] text-[#F4F1EA]">
          {user?.name || user?.fullName || user?.username || 'Utilisateur'}
        </p>
        {vehicleCount != null && (
          <p className="text-[11px] text-[#8F877B] mt-[3px] tabular-nums">
            {vehicleCount} véhicule{vehicleCount > 1 ? 's' : ''}
          </p>
        )}
      </div>

      <nav className="flex flex-col gap-[18px]">
        {sections.map(section => (
          <div key={section.title} className="flex flex-col gap-0.5">
            <p className="text-[9px] uppercase tracking-[.2em] text-[#5C554C] px-2.5 pb-[7px]">{section.title}</p>
            {section.items.map(item => {
              const Icon = item.icon;
              return (
                <NavLink key={item.path} to={item.path} className={linkClass}>
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="absolute left-0 top-[9px] bottom-[9px] w-0.5 rounded-sm bg-gradient-to-b from-[#F4F1EA] to-[#8A8780]" />
                      )}
                      <Icon className="w-[17px] h-[17px] shrink-0" />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="text-[10px] font-bold text-[#0D0C0B] bg-[#E2574B] rounded-full px-1.5 tabular-nums">
                          {item.badge}
                        </span>
                      )}
                      {!item.badge && item.count != null && (
                        <span className="text-[10.5px] text-[#6E665C] tabular-nums">{item.count}</span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="mt-auto pt-3.5 border-t border-[#211E1B] flex flex-col gap-0.5">
        <NavLink to="/support" className="flex items-center gap-[11px] px-2.5 py-2 rounded-[9px] text-[12.5px] text-[#7C7468] hover:bg-[#171512] hover:text-[#F4F1EA] transition-colors">
          <IconHelp className="w-4 h-4" /> Aide &amp; support
        </NavLink>
        <button onClick={onLogout}
          className="flex items-center gap-[11px] px-2.5 py-2 rounded-[9px] text-[12.5px] text-[#7C7468] hover:bg-[#1E1512] hover:text-[#E2574B] transition-colors text-left">
          <IconLogout className="w-4 h-4" /> Déconnexion
        </button>
      </div>
    </aside>
  );
}
